import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ArrowLeft, Check, Receipt } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useT } from "@/context/I18nContext";

const TIERS = [
  { qty: 10,  discount: 25, label: "Starter" },
  { qty: 50,  discount: 30, label: "Growth"  },
  { qty: 100, discount: 35, label: "Scale"   },
];

const PLAN_OPTIONS = [
  { key: "storage_1tb_lifetime", label: "1 TB", eur: 60 },
  { key: "storage_2tb_lifetime", label: "2 TB", eur: 100 },
  { key: "storage_5tb_lifetime", label: "5 TB", eur: 200 },
];

export default function BusinessLanding() {
  const nav = useNavigate();
  const { locale } = useT();
  const [form, setForm] = useState({
    company_name: "",
    vat_number: "",
    contact_name: "",
    email: "",
    quantity: 10,
    plan_lookup_key: "storage_1tb_lifetime",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/business/quote", { ...form, locale });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const plan = PLAN_OPTIONS.find((p) => p.key === form.plan_lookup_key);
  const listTotal = plan.eur * form.quantity;
  const tier = TIERS.filter((t) => form.quantity >= t.qty).slice(-1)[0];
  const discount = tier?.discount || (form.quantity >= 5 ? 20 : form.quantity >= 3 ? 15 : form.quantity === 2 ? 10 : 0);
  const total = Math.round(listTotal * (1 - discount / 100));

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <button onClick={() => nav("/")} className="mb-6 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> {locale === "en" ? "Back to home" : "Torna alla home"}
        </button>

        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-300">
              <Building2 className="h-3.5 w-3.5" /> {locale === "en" ? "NEXUSCLOUD BUSINESS" : "NEXUSCLOUD BUSINESS"}
            </div>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
              {locale === "en" ? "Team & enterprise" : "Team & aziende"}
              <br />
              <span className="text-gradient">{locale === "en" ? "lifetime storage" : "storage a vita"}</span>
            </h1>
            <p className="mt-4 max-w-lg text-slate-300">
              {locale === "en"
                ? "Buy 10 to 500 lifetime codes in one order. Volume discount up to 35%, electronic invoice with your VAT number, dedicated onboarding."
                : "Acquista da 10 a 500 codici a vita in un solo ordine. Sconto volume fino al 35%, fattura elettronica con la tua P.IVA, onboarding dedicato."}
            </p>

            <div className="mt-8 space-y-3">
              {TIERS.map((tt) => (
                <div key={tt.qty} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-400/10 text-cyan-300">
                    <span className="mono text-sm font-bold">{tt.qty}+</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{tt.label}</div>
                    <div className="text-xs text-slate-400">
                      {locale === "en" ? "codes from" : "codici da"} {tt.qty}
                    </div>
                  </div>
                  <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-bold text-emerald-300">
                    -{tt.discount}%
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-2 text-sm text-slate-300">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-300" />{locale === "en" ? "Volume discount up to 35%" : "Sconto volume fino al 35%"}</div>
              <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-cyan-300" />{locale === "en" ? "E-invoice with VAT (Italy: FE)" : "Fattura elettronica con P.IVA (FE Italia)"}</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-300" />{locale === "en" ? "Dedicated account manager" : "Account manager dedicato"}</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-300" />{locale === "en" ? "Payment via bank transfer or Stripe" : "Pagamento via bonifico o Stripe"}</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-transparent blur-2xl" />
            <div className="glass relative rounded-2xl p-6 glow-cyan">
              {sent ? (
                <div data-testid="business-sent-msg" className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-6 text-center">
                  <Check className="mx-auto h-12 w-12 text-cyan-300" />
                  <h3 className="mt-3 text-xl font-bold">
                    {locale === "en" ? "Request sent!" : "Richiesta inviata!"}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300">
                    {locale === "en"
                      ? "Our team will reply within 24 hours with a personalized quote and invoice."
                      : "Il nostro team ti risponderà entro 24 ore con un preventivo personalizzato e la fattura."}
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <h3 className="text-xl font-bold">{locale === "en" ? "Get a quote" : "Richiedi preventivo"}</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300 text-xs">{locale === "en" ? "Company" : "Azienda"}</Label>
                      <Input required data-testid="b2b-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="border-slate-800 bg-slate-950 text-slate-100" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{locale === "en" ? "VAT number" : "Partita IVA"}</Label>
                      <Input required data-testid="b2b-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="border-slate-800 bg-slate-950 text-slate-100" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{locale === "en" ? "Contact" : "Referente"}</Label>
                      <Input required data-testid="b2b-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="border-slate-800 bg-slate-950 text-slate-100" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">Email</Label>
                      <Input type="email" required data-testid="b2b-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-800 bg-slate-950 text-slate-100" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{locale === "en" ? "Plan" : "Piano"}</Label>
                      <select data-testid="b2b-plan" value={form.plan_lookup_key} onChange={(e) => setForm({ ...form, plan_lookup_key: e.target.value })} className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                        {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{locale === "en" ? "Quantity" : "Quantita"}</Label>
                      <Input type="number" min={1} max={500} required data-testid="b2b-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Math.max(1, Math.min(500, parseInt(e.target.value) || 1)) })} className="border-slate-800 bg-slate-950 text-slate-100" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">{locale === "en" ? "Notes (optional)" : "Note (opzionale)"}</Label>
                    <Textarea rows={3} data-testid="b2b-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-slate-800 bg-slate-950 text-slate-100" />
                  </div>

                  <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{locale === "en" ? "List price" : "Prezzo listino"}</span>
                      <span className="text-slate-500 line-through">€{listTotal}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cyan-300">-{discount}% {locale === "en" ? "volume" : "volume"}</span>
                      <span className="text-cyan-300">-€{listTotal - total}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-800 pt-2">
                      <span className="text-sm font-semibold">{locale === "en" ? "Estimated total" : "Totale stimato"}</span>
                      <span data-testid="b2b-total" className="text-2xl font-extrabold text-cyan-300">€{total}</span>
                    </div>
                  </div>

                  <Button type="submit" disabled={busy} data-testid="b2b-submit-btn" className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300">
                    {busy ? (locale === "en" ? "Sending..." : "Invio...") : (locale === "en" ? "Request quote" : "Richiedi preventivo")}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
